import styled from "styled-components";
import React from 'react';
import { BarChart3 } from 'lucide-react';
import { colors } from "../../utils/colors";
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { TextButtonXS } from '../Controls/Buttons';
import { useTranslation } from 'react-i18next';

// Main container - unified rounded rectangle matching ReliabilityScoreBadge
export const StatContainer = styled.div<{ $clickable?: boolean; onClick?: (event: React.MouseEvent) => void }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  border-radius: 1rem;
  background: ${slateColors[50]};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  overflow-x: auto;
  overflow-y: hidden;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  scrollbar-width: none; /* Firefox - hide by default */
  -ms-overflow-style: none; /* IE and Edge - hide by default */
  
  /* Webkit scrollbar - hide by default */
  &::-webkit-scrollbar {
    height: 6px;
    display: none;
  }
  
  /* Show scrollbar on hover or when scrolling */
  &:hover::-webkit-scrollbar,
  &:active::-webkit-scrollbar {
    display: block;
  }
  
  &:hover {
    scrollbar-width: thin; /* Firefox - show on hover */
    scrollbar-color: ${slateColors[400]} ${slateColors[50]};
  }
  
  &::-webkit-scrollbar-track {
    background: ${slateColors[50]};
    border-radius: 0 0 1rem 1rem;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${slateColors[400]};
    border-radius: 3px;
    
    &:hover {
      background: ${slateColors[500]};
    }
  }
  
  ${props => props.$clickable && `
    cursor: pointer;
    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    }
  `}
`;

// Stats container - horizontal row of stat boxes
export const StatsContainer = styled.div<{ $flex?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  ${props => props.$flex && `flex: 1;`}
`;

// Stat box - integrated into container with borders between
export const StatBox = styled.div<{ $flex?: boolean; $gap?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 0.625rem 0.75rem;
  min-width: 60px;
  background: transparent;
  border-left: 1px solid ${slateColors[200]};
  gap: 0.375rem;
  ${props => props.$flex && `flex: 1;`}
  
  &:first-child {
    border-left: none;
  }
`;

export const StatTopSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
`;

export const StatIconWrapper = styled.div<{ $iconColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.grey600};
  flex-shrink: 0;
  
  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

export const StatTitle = styled.div<{ $white?: boolean }>`
  color: ${props => props.$white ? colors.white : slateColors[900]};
  font-size: 0.625rem;
  font-weight: ${props => props.$white ? 700 : 600};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
`;

export const StatValue = styled.div<{ $valueColor?: string; $large?: boolean; $white?: boolean }>`
  color: ${props => props.$white ? colors.white : (props.$valueColor || slateColors[900])};
  font-size: ${props => props.$large ? '1.5rem' : '1rem'};
  font-weight: 600;
  line-height: ${props => props.$large ? '1.2' : '1'};
  letter-spacing: ${props => props.$large ? '-0.02em' : '-0.01em'};
  display: flex;
  align-items: center;
`;

export const StatButtonContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  border-left: 1px solid ${slateColors[200]};
  background: transparent;
  transition: background-color 0.2s;
  
  /* Hover effect is handled by parent StatContainer */
`;

// Shared Insights Button Component (no longer needs onClick, parent handles it)
type InsightsButtonProps = {
  text?: string;
};

export const InsightsButton: React.FC<InsightsButtonProps> = ({ text }) => {
  const { t } = useTranslation();
  const displayText = text || t('elements.sharedStatComponents.insights');
  
  return (
    <StatButtonContainer>
      <TextButtonXS style={{ flexDirection: 'column', gap: '0.2rem', alignItems: 'center' }}>
        <BarChart3 size={14} />
        {displayText}
      </TextButtonXS>
    </StatButtonContainer>
  );
};

// Reliability box - colored background for reliability score
export const ReliabilityBox = styled.div<{ $bgColor: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 0.625rem 0.75rem;
  min-width: 80px;
  background: ${props => props.$bgColor};
  flex-shrink: 0;
  gap: 0.125rem;
`;

// Helper to format percentage
export const formatPercentage = (value: number): string => {
  return `${Math.round(value)}%`;
};

// Helper to format delay in minutes
export const formatDelayMinutes = (delaySeconds: number): string => {
  const minutes = Math.round(delaySeconds / 60);
  return minutes >= 0 ? `+${minutes}m` : `${minutes}m`;
};

// Helper to get color variants for stat values
export const getColorVariants = (baseColor: string) => {
  const darkVariant: Record<string, string> = {
    [colors.red]: colors.red,
    [colors.orange]: colors.orange,
    [colors.green]: colors.green,
    [colors.grey600]: colors.grey800,
  };
  return darkVariant[baseColor] || slateColors[900];
};

